import { createEffect, createSignal, For, Show } from 'solid-js'
import AccountSidebar from './AccountSidebar'
import HeaderContainer from '/web/shared/aifans/HeaderContainer'
import Footer from '/web/shared/aifans/Footer'
import { chatStore, toastStore } from '/web/store'
import { AIFansSubscription, SubscriptionEvent } from '/common/types/aifans-schema'
import { aifansSubscriptionApi } from '/web/store/data/aifans-subscription'
import { useNavigate, useParams, A } from '@solidjs/router'
import CancellationModal from './CancellationModal'
import ReactivationModal from './ReactivationAdminModal'
import { formatTransactionType } from '/web/shared/aifans/lib/transaction-util'
import { getCancellationDescription } from '../../../common/aifans/lib/cancellation-util'
import { getModelUrl } from '/web/shared/aifans/lib/model-util'

export default function AdminSubscriptions() {
  const [tableData, setTableData] = createSignal<AIFansSubscription[]>([])
  const [totalPages, setTotalPages] = createSignal(1)
  const [sortKey, setSortKey] = createSignal<keyof AIFansSubscription | null>(null)
  const [sortDirection, setSortDirection] = createSignal<'asc' | 'desc'>('desc')
  const [showCancellationModal, setShowCancellationModal] = createSignal(false)
  const [cancellingCharId, setCancellingCharId] = createSignal<string>()
  const [cancellingId, setCancellingId] = createSignal<string>()
  const [showReactivateModal, setShowReactivateModal] = createSignal(false)
  const [reactivatingId, setReactivatingId] = createSignal<string>('')
  const [isReactivating, setIsReactivating] = createSignal(false)
  const [userId, setUserId] = createSignal<string>('')
  const [expandedRows, setExpandedRows] = createSignal<Set<string>>(new Set())
  const [subscriptionEvents, setSubscriptionEvents] = createSignal<
    Record<string, SubscriptionEvent[]>
  >({})
  const [loadingEvents, setLoadingEvents] = createSignal<Set<string>>(new Set())

  const params = useParams()
  const navigate = useNavigate()
  const pageSize = 20

  const characterFromId = (characterId: string) => {
    const char = chatStore().allChars.list.find((c) => c._id === characterId)
    return char
  }

  createEffect(async () => {
    const currentPage = Number(params.page) || 1
    await loadData(currentPage)
  })

  const loadData = async (page: number) => {
    try {
      const res = await aifansSubscriptionApi.getAdminSubscriptions(page, pageSize)
      if (res.result) {
        if (sortKey()) {
          const sorted = getSortedData(res.result.subscriptions, sortKey()!, sortDirection())
          setTableData(sorted)
        } else {
          setTableData(res.result.subscriptions)
        }
        setTotalPages(Math.ceil(res.result.totalCount / pageSize))
      }
    } catch (error) {
      console.error(error)
      toastStore.error('Failed to load subscriptions')
    }
  }

  const changePage = (page: number) => {
    navigate(page === 1 ? '/admin/subscriptions' : `/admin/subscriptions/${page}`)
  }

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(dateString))
  }

  const cancelSubscription = async (characterId: string, userId: string) => {
    setCancellingCharId(characterId)
    setShowCancellationModal(true)
    setUserId(userId)
  }

  const handleCancellationConfirm = async (reason: string) => {
    const characterId = cancellingCharId()
    if (!characterId) return

    setCancellingId(characterId)
    try {
      const res = await aifansSubscriptionApi.cancelAIFansAdminSubscription(
        characterId,
        userId(),
        reason
      )
      if (res.result?.success) {
        await loadData(Number(params.page) || 1)
        toastStore.success('Subscription cancelled!')
      } else {
        throw new Error('Failed to cancel subscription')
      }
    } catch (error) {
      console.error(error)
      toastStore.error('Failed to cancel subscription')
    } finally {
      setCancellingId(undefined)
      setUserId('')
      setCancellingCharId(undefined)
      setShowCancellationModal(false)
    }
  }

  const reactivateSubscription = async (characterId: string, userId: string) => {
    setReactivatingId(characterId)
    setShowReactivateModal(true)
    setUserId(userId)
  }

  const resetModal = () => {
    setShowReactivateModal(false)
    setReactivatingId('')
    setUserId('')
  }

  const confirmReactivation = async () => {
    const characterId = reactivatingId()
    if (!characterId) return

    setIsReactivating(true)
    try {
      const res = await aifansSubscriptionApi.reactivateAdminSubscription(characterId, userId())
      if (res.result?.success) {
        await loadData(Number(params.page) || 1)
        toastStore.success('Subscription reactivated!')
      } else {
        throw new Error('Failed to reactivate subscription')
      }
    } catch (error) {
      console.error(error)
      toastStore.error('Failed to reactivate subscription')
    } finally {
      setIsReactivating(false)
      resetModal()
    }
  }

  const toggleRow = async (subscriptionId: string) => {
    const newSet = new Set(expandedRows())
    if (newSet.has(subscriptionId)) {
      newSet.delete(subscriptionId)
    } else {
      newSet.add(subscriptionId)
      if (!subscriptionEvents()[subscriptionId]) {
        setLoadingEvents((prev) => new Set(prev).add(subscriptionId))
        try {
          const { result } = await aifansSubscriptionApi.getSubscriptionEvents(subscriptionId)
          if (result) {
            setSubscriptionEvents((prev) => ({ ...prev, [subscriptionId]: result.result }))
          }
        } catch (error) {
          console.error(error)
          toastStore.error('Failed to load subscription events')
        } finally {
          setLoadingEvents((prev) => {
            const next = new Set(prev)
            next.delete(subscriptionId)
            return next
          })
        }
      }
    }
    setExpandedRows(newSet)
  }

  const renderTableRow = (sub: AIFansSubscription) => {
    const isExpanded = () => expandedRows().has(sub._id)
    const events = () => subscriptionEvents()[sub._id] || []

    return (
      <>
        <tr class="border-t border-gray-700" data-subscription-id={sub._id}>
          <td class="py-4" data-user-id={sub.userId}>
            <button
              onClick={() => toggleRow(sub._id)}
              class="hover:text-cyan-600 mr-2 text-xxs text-gray-400"
            >
              {isExpanded() ? '▼' : '▶'}
            </button>
            {sub.username}
          </td>
          <td class="py-4" data-character-id={sub.characterId}>
            <A
              href={getModelUrl(
                characterFromId(sub.characterId)?.categorySlug,
                characterFromId(sub.characterId)?.slug
              )}
              class="text-[#10e0f9] hover:underline"
            >
              <div class="text-base">{characterFromId(sub.characterId)?.name}</div>
              {characterFromId(sub.characterId)?.realName && (
                <div class="text-xs text-gray-400">
                  {characterFromId(sub.characterId)?.realName}
                </div>
              )}
            </A>
          </td>
          <td class="py-4">{formatDate(sub.startDate)}</td>
          <td class="py-4">{formatDate(sub.endDate)}</td>
          <td class="py-4">${sub.amount}</td>
          <td class="py-4">{sub.tokens}</td>
          <td class={`py-4 ${getStatus(sub).class}`}>{getStatus(sub).text}</td>
          <td class="py-4">
            <Show when={sub.status === 'cancelled' || sub.status === 'reactivating'}>
              <button
                class="text-cosplay-green"
                onClick={() => reactivateSubscription(sub.characterId, sub.userId)}
              >
                Activate
              </button>
            </Show>
            <Show when={sub.status === 'active'}>
              <button
                class="text-cosplay-red"
                onClick={() => cancelSubscription(sub.characterId, sub.userId)}
                disabled={cancellingId() === sub.characterId}
              >
                {cancellingId() === sub.characterId ? (
                  <span class="flex items-center gap-2">
                    <div class="h-4 w-4 animate-spin rounded-full border-2 border-cosplay-red border-t-transparent"></div>
                    Cancelling...
                  </span>
                ) : (
                  'Cancel'
                )}
              </button>
            </Show>
          </td>
        </tr>
        <Show when={isExpanded()}>
          <tr>
            <td colspan="7" class="bg-gray-800 p-4">
              <Show
                when={!loadingEvents().has(sub._id)}
                fallback={
                  <div class="flex justify-center py-4">
                    <div class="h-6 w-6 animate-spin rounded-full border-2 border-cyan border-t-transparent"></div>
                  </div>
                }
              >
                <Show
                  when={events().length > 0}
                  fallback={<div class="py-4 text-center text-gray-400">No events found</div>}
                >
                  <table class="w-full">
                    <thead>
                      <tr class="text-sm">
                        <th class="py-2 text-left">Date</th>
                        <th class="py-2 text-left">Type</th>
                        <th class="py-2 text-left">Amount</th>
                        <th class="py-2 text-left">Status</th>
                        <th class="py-2 text-left">Method</th>
                        <th class="py-2 text-left">Member ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={events()}>
                        {(event) => (
                          <tr class="text-sm" data-transaction-id={event._id}>
                            <td class="py-2">{formatDate(event.date)}</td>
                            <td class="py-2">{formatTransactionType(event.type)}</td>
                            <td class="py-2">{event.amount ? `$${event.amount}` : '-'}</td>
                            <td class="py-2">{event.status || event.reasonDescription || '-'}</td>
                            <td class="py-2">
                              {(event.paymentType &&
                                `${event.paymentType} - ${event.paymentSubtype}`) ||
                                '-'}
                            </td>
                            <td class="py-2">{event.memberId || '-'}</td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </Show>
              </Show>
            </td>
          </tr>
        </Show>
      </>
    )
  }

  // Sorting logic
  const sortData = (key: keyof AIFansSubscription, initialDirection?: 'asc' | 'desc') => {
    if (sortKey() === key) {
      setSortDirection(sortDirection() === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection(initialDirection || 'asc')
    }

    const sorted = getSortedData(tableData(), key, sortDirection())
    setTableData(sorted)
  }

  const getSortedData = (
    data: AIFansSubscription[],
    key: keyof AIFansSubscription,
    direction: 'asc' | 'desc'
  ) => {
    return [...data].sort((a, b) => {
      let aValue: any = a[key]
      let bValue: any = b[key]

      if (key === 'amount') {
        aValue = parseFloat(a.amount.toString())
        bValue = parseFloat(b.amount.toString())
        if (isNaN(aValue)) aValue = Infinity
        if (isNaN(bValue)) bValue = Infinity
      }

      if (key === 'startDate' || key === 'endDate') {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      }

      if (key === 'status') {
        aValue = a.status
        bValue = b.status
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1
      if (aValue > bValue) return direction === 'asc' ? 1 : -1
      return 0
    })
  }

  const renderSortIndicator = (key: keyof AIFansSubscription) => {
    if (sortKey() === key) {
      return <div class="text-xxs">{sortDirection() === 'asc' ? ' ▲' : ' ▼'}</div>
    }
    return ''
  }

  const getStatus = (subscription: AIFansSubscription) => {
    switch (subscription.status) {
      case 'active':
        return { text: 'Active', class: 'text-cosplay-green' }
      case 'cancelled':
        return { text: 'Cancelled', class: 'text-red' }
      case 'expired':
        return { text: 'Expired', class: 'text-red' }
      case 'reactivating':
        return { text: 'Reactivating...', class: 'text-yellow-400' }
      case 'restarted':
        return { text: 'Restarted', class: 'text-gray-400' }
      default:
        return { text: 'Unknown', class: 'text-gray-400' }
    }
  }

  const renderPagination = () => {
    const current = Number(params.page || '1')
    const total = totalPages()
    const delta = 2
    const range: (number | string)[] = []

    range.push(1)
    let start = Math.max(2, current - delta)
    let end = Math.min(total - 1, current + delta)

    if (current - delta <= 2) {
      end = Math.min(total - 1, 1 + 2 * delta)
    }
    if (current + delta >= total - 1) {
      start = Math.max(2, total - 2 * delta)
    }

    if (start > 2) range.push('...')
    for (let i = start; i <= end; i++) {
      range.push(i)
    }
    if (end < total - 1) range.push('...')
    if (total > 1) range.push(total)

    return (
      <div class="mt-4 flex items-center justify-center gap-2">
        <button
          onClick={() => current > 1 && changePage(current - 1)}
          disabled={current === 1}
          class={`rounded px-3 py-1 ${
            current === 1 ? 'cursor-not-allowed opacity-50' : ''
          } bg-gray-600`}
        >
          ←
        </button>

        <For each={range}>
          {(page) => {
            if (page === '...') {
              return <span class="px-3 py-1">...</span>
            }
            return (
              <button
                onClick={() => changePage(page as number)}
                class={`rounded px-3 py-1 ${
                  current === page ? 'bg-cyan text-black' : 'bg-gray-600'
                }`}
              >
                {page}
              </button>
            )
          }}
        </For>

        <button
          onClick={() => current < total && changePage(current + 1)}
          disabled={current === total}
          class={`rounded px-3 py-1 ${
            current === total ? 'cursor-not-allowed opacity-50' : ''
          } bg-gray-600`}
        >
          →
        </button>
      </div>
    )
  }

  return (
    <>
      <HeaderContainer />
      <main class="page-bg relative overflow-hidden bg-main pb-20 pt-20 sm:pt-28 md:bg-none md:pb-36">
        <div class="container relative z-20">
          <div class="wrapper gap-8 lg:flex">
            <AccountSidebar />
            <div class="right flex w-full flex-col">
              <div class="head gray-to-tran mb-3 rounded-xl text-2xl font-semibold sm:mb-7 sm:block sm:p-4 sm:text-[28px]">
                <h2>Admin Subscriptions</h2>
              </div>
              <div class="gray-to-tran h-full w-full rounded-xl sm:p-6">
                {tableData().length ? (
                  <div class="mt-6 inline-block w-full overflow-x-auto font-normal">
                    <table class="w-full table-auto">
                      <thead>
                        <tr>
                          <th class="py-3 text-left">User</th>
                          <th
                            onClick={() => sortData('characterId')}
                            class="cursor-pointer py-3 text-left"
                          >
                            <div class="flex items-center gap-2">
                              Character {renderSortIndicator('characterId')}
                            </div>
                          </th>
                          <th
                            onClick={() => sortData('startDate')}
                            class="cursor-pointer py-3 text-left"
                          >
                            <div class="flex items-center gap-2">
                              Start Date {renderSortIndicator('startDate')}
                            </div>
                          </th>
                          <th
                            onClick={() => sortData('endDate')}
                            class="cursor-pointer py-3 text-left"
                          >
                            <div class="flex items-center gap-2">
                              End Date {renderSortIndicator('endDate')}
                            </div>
                          </th>
                          <th
                            onClick={() => sortData('amount')}
                            class="cursor-pointer py-3 text-left"
                          >
                            <div class="flex items-center gap-2">
                              Amount {renderSortIndicator('amount')}
                            </div>
                          </th>
                          <th
                            onClick={() => sortData('tokens')}
                            class="cursor-pointer py-3 text-left"
                          >
                            <div class="flex items-center gap-2">
                              Tokens {renderSortIndicator('tokens')}
                            </div>
                          </th>
                          <th
                            onClick={() => sortData('status')}
                            class="cursor-pointer py-3 text-left"
                          >
                            <div class="flex items-center gap-2">
                              Status {renderSortIndicator('status')}
                            </div>
                          </th>
                          <th class="py-3 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <For each={tableData()}>{(sub) => renderTableRow(sub)}</For>
                      </tbody>
                    </table>
                    {renderPagination()}
                  </div>
                ) : (
                  <div class="empty-state py-8 text-center">
                    <p class="text-xl text-gray-400">No subscriptions found.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <CancellationModal
        show={showCancellationModal()}
        onClose={() => {
          setShowCancellationModal(false)
          setCancellingCharId(undefined)
        }}
        onConfirm={handleCancellationConfirm}
        characterName={cancellingCharId() ? characterFromId(cancellingCharId()!)?.name : undefined}
        loading={cancellingId() === cancellingCharId()}
      />
      <ReactivationModal
        show={showReactivateModal()}
        onClose={resetModal}
        onConfirm={confirmReactivation}
        loading={isReactivating()}
      />
    </>
  )
}
